import StoreUpdateEvents from "../constants/events/StoreUpdateEvents";
import SecureBaseStore from "./SecureBaseStore";
import UIInteractionEvents from "../constants/events/UIInteractionEvents";
import ActionResultEvents from "../constants/events/ActionResultEvents";
import SavingStates from "../constants/states/SavingStates";
import {FormFieldWithValidityState} from "./FormFieldWithValidityState";


class SpaceCreateViewState {
    constructor(
        savingState,
        title,
        description,
        price,
        minimumDays,
        spaceAddress,
        spaceAddressGooglePlaceId,
        spaceAddressLatitude,
        spaceAddressLongitude
    ) {
        this.savingState = savingState;
        this.title = title;
        this.description = description;
        this.price = price;
        this.minimumDays = minimumDays;
        this.spaceAddress = spaceAddress;
        this.spaceAddressGooglePlaceId = spaceAddressGooglePlaceId;
        this.spaceAddressLatitude = spaceAddressLatitude;
        this.spaceAddressLongitude = spaceAddressLongitude;
    }

    equals(other) {
        return this.savingState === other.savingState &&
            this.title.equals(other.title) &&
            this.description.equals(other.description) &&
            this.price.equals(other.price) &&
            this.minimumDays.equals(other.minimumDays) &&
            this.spaceAddress === other.spaceAddress &&
            this.spaceAddressGooglePlaceId.equals(other.spaceAddressGooglePlaceId) &&
            this.spaceAddressLatitude === other.spaceAddressLatitude &&
            this.spaceAddressLongitude === other.spaceAddressLongitude;
    }
}

class ImmutableSpaceCreateViewStateBuilder {

    constructor() {
        this.savingState = SavingStates.NOT_STARTED;
        this.title = new FormFieldWithValidityState("", false, false);;
        this.description = new FormFieldWithValidityState("", false, false);
        this.price = new FormFieldWithValidityState("", false, false);
        this.minimumDays = new FormFieldWithValidityState(1, true, false);
        this.spaceAddress = "";
        this.spaceAddressGooglePlaceId = FormFieldWithValidityState.initialDefaults("");
        this.spaceAddressLatitude = 0;
        this.spaceAddressLongitude = 0;
    }

    static fromState(state) {
        return new ImmutableSpaceCreateViewStateBuilder()
            .setSavingState(state.savingState)
            .setTitle(state.title.currentValue, state.title.isValid, state.title.isTouched)
            .setDescription(state.description.currentValue, state.description.isValid, state.description.isTouched)
            .setPrice(state.price.currentValue, state.price.isValid, state.price.isTouched)
            .setMinimumDays(state.minimumDays.currentValue, state.minimumDays.isValid, state.minimumDays.isTouched)
            .setSpaceAddress(state.spaceAddress)
            .setSpaceAddressGooglePlaceId(
                state.spaceAddressGooglePlaceId.currentValue,
                state.spaceAddressGooglePlaceId.isValid,
                state.spaceAddressGooglePlaceId.isTouched)
            .setSpaceAddressLatitude(state.spaceAddressLatitude)
            .setSpaceAddressLongitude(state.spaceAddressLongitude);
    }

    setSavingState(newState) {
        this.savingState = newState;
        return this;
    }

    setTitle(title, isValid, isTouched) {
        this.title = new FormFieldWithValidityState(title, isValid, isTouched);
        return this;
    }

    setDescription(description, isValid, isTouched) {
        this.description = new FormFieldWithValidityState(description, isValid, isTouched);
        return this;
    }

    setPrice(price, isValid, isTouched) {
        this.price = new FormFieldWithValidityState(price, isValid, isTouched);
        return this;
    }

    setMinimumDays(minimumDays, isValid, isTouched) {
        this.minimumDays = new FormFieldWithValidityState(minimumDays, isValid, isTouched);
        return this;
    }

    setSpaceAddressGooglePlaceId(spaceAddressGooglePlaceId, isValid, isTouched) {
        this.spaceAddressGooglePlaceId = new FormFieldWithValidityState(spaceAddressGooglePlaceId, isValid, isTouched);
        return this;
    }

    setSpaceAddressLatitude(latitude) {
        this.spaceAddressLatitude = latitude;
        return this;
    }

    setSpaceAddressLongitude(longitude) {
        this.spaceAddressLongitude = longitude;
        return this;
    }

    setSpaceAddress(spaceAddress) {
        this.spaceAddress = spaceAddress;
        return this;
    }

    resetSpaceAddress() {
        this.spaceAddress = "";
        this.spaceAddressGooglePlaceId = FormFieldWithValidityState.initialDefaults("");
        this.spaceAddressLatitude = 0;
        this.spaceAddressLongitude = 0;
        return this;
    }

    build() {
        return Object.freeze(
            new SpaceCreateViewState(
                this.savingState,
                this.title,
                this.description,
                this.price,
                this.minimumDays,
                this.spaceAddress,
                this.spaceAddressGooglePlaceId,
                this.spaceAddressLatitude,
                this.spaceAddressLongitude));
    }
}


class SpaceCreateViewStore extends SecureBaseStore {

    constructor() {
        super(StoreUpdateEvents.SPACE_CREATE_VIEW_STORE_UPDATED, ImmutableSpaceCreateViewStateBuilder);
        this.subscribeToEvent(UIInteractionEvents.SPACE_CREATE_TITLE_CHANGED, this.handleTitleChanged);
        this.subscribeToEvent(UIInteractionEvents.SPACE_CREATE_DESCRIPTION_CHANGED, this.handleDescriptionChanged);
        this.subscribeToEvent(UIInteractionEvents.SPACE_CREATE_PRICE_CHANGED, this.handlePriceChanged);
        this.subscribeToEvent(UIInteractionEvents.SPACE_CREATE_MINIMUM_DAYS_CHANGED, this.handleMinimumDaysChanged);
        this.subscribeToEvent(UIInteractionEvents.SPACE_CREATE_ADDRESS_AUTOCOMPLETE_CHANGED, this.handleGoogleAutocompleteChanged);
        this.subscribeToEvent(UIInteractionEvents.SPACE_CREATE_ADDRESS_AUTOCOMPLETE_CLEARED, this.handleGoogleAutocompleteCleared);
        this.subscribeToEvent(UIInteractionEvents.SPACE_CREATE_SAVE_SUBMITTED, this.handleSaveStarted);
        this.subscribeToEvent(ActionResultEvents.SPACE_CREATED, this.resetStore);
    }

    handleSaveStarted = (topic, data) => {
        return ImmutableSpaceCreateViewStateBuilder.fromState(this.state)
            .setSavingState(SavingStates.SAVING)
            .build();
    };

    handleTitleChanged = (topic, data) => {
        return ImmutableSpaceCreateViewStateBuilder.fromState(this.state)
            .setTitle(data.title, data.isValid, true)
            .build();
    };

    handleDescriptionChanged = (topic, data) => {
        return ImmutableSpaceCreateViewStateBuilder.fromState(this.state)
            .setDescription(data.description, data.isValid, true)
            .build();
    };

    handlePriceChanged = (topic, data) => {
        return ImmutableSpaceCreateViewStateBuilder.fromState(this.state)
            .setPrice(data.price, data.isValid, true)
            .build();
    };

    handleMinimumDaysChanged = (topic, data) => {
        return ImmutableSpaceCreateViewStateBuilder.fromState(this.state)
            .setMinimumDays(data.minimumDays, data.isValid, true)
            .build();
    };

    handleGoogleAutocompleteChanged = (topic, data) => {
        return ImmutableSpaceCreateViewStateBuilder.fromState(this.state)
            .setSpaceAddress(data.address)
            .setSpaceAddressGooglePlaceId(
                data.placeId,
                data.isValid,
                true)
            .setSpaceAddressLatitude(data.latitude)
            .setSpaceAddressLongitude(data.longitude)
            .build();
    };

    handleGoogleAutocompleteCleared = (topic, data) => {
        return ImmutableSpaceCreateViewStateBuilder.fromState(this.state)
            .resetSpaceAddress()
            .build()
    }



}

export default new SpaceCreateViewStore();